import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router';

import { getRoles } from '@/app/auth/claims';
import { PORTAL_AUTHORIZED_ROLES } from '@/app/auth/knownRoles';
import { AuthenticationPageLayout } from '@/app/shell/AuthenticationPageLayout';

export type AuthGuardState = 'authorizing' | 'redirect-to-login' | 'not-authorized' | 'authorized';

export function resolveAuthGuardState(
  isLoading: boolean,
  isAuthenticated: boolean,
  userRoles: readonly string[],
  authorizedRoles: readonly string[] = PORTAL_AUTHORIZED_ROLES,
): AuthGuardState {
  if (isLoading) {
    return 'authorizing';
  }

  if (!isAuthenticated) {
    return 'redirect-to-login';
  }

  if (!authorizedRoles.some((role) => userRoles.includes(role))) {
    return 'not-authorized';
  }

  return 'authorized';
}

function RedirectToLogin() {
  const location = useLocation();
  const returnUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;

  return <Navigate to="/authentication/login" state={{ returnUrl }} replace />;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { t } = useTranslation();

  const guardState = resolveAuthGuardState(auth.isLoading, auth.isAuthenticated, getRoles(auth.user));

  switch (guardState) {
    case 'authorizing':
      return (
        <AuthenticationPageLayout>
          <p className="fade-in-delay">
            {t('Authentication_Authorizing_Label', { defaultValue: 'Authorizing user' })}
          </p>
        </AuthenticationPageLayout>
      );

    case 'redirect-to-login':
      return <RedirectToLogin />;

    case 'not-authorized':
      return (
        <AuthenticationPageLayout>
          <p role="alert">You are not authorized to access this resource.</p>
        </AuthenticationPageLayout>
      );

    case 'authorized':
      return <>{children}</>;
  }
}
